/**
 * TEXTOS EN ESPAÑOL E INGLÉS
 * =============================================================================
 * Todo el texto de la página vive aquí, en los dos idiomas.
 *
 * -----------------------------------------------------------------------------
 * POR QUÉ SEPARADO DEL COMPONENTE
 * -----------------------------------------------------------------------------
 * Para que traducir o corregir una frase no obligue a tocar el diseño. Y para que
 * el selector ES | EN pueda cambiar TODO de verdad: si un texto se quedara escrito
 * dentro de un componente, al cambiar de idioma seguiría en español y se notaría.
 *
 * Los textos están copiados de las referencias. Los que no se leían con claridad
 * están marcados con un comentario para poder revisarlos.
 */

export const TEXTOS = {
  es: {
    /**
     * EL TÍTULO DE LA PESTAÑA Y LA DESCRIPCIÓN
     *
     * -----------------------------------------------------------------------------
     * POR QUÉ HACE FALTA TRADUCIRLOS
     * -----------------------------------------------------------------------------
     * El `index.html` los trae escritos en español, porque es el idioma por defecto
     * y es el que leen los buscadores al indexar la página. Eso está bien.
     *
     * Pero el título de la pestaña también lo ve quien está leyendo. Si alguien pone
     * la página en inglés, no puede encontrarse «Formación integral de futbolistas»
     * arriba del navegador.
     *
     * Así que al cambiar de idioma se reescriben. El HTML sigue en español para los
     * buscadores y la pestaña se traduce para el usuario.
     */
    metaTitulo: 'M SOCCER ACADEMY · Más que fútbol',
    metaDescripcion:
      'Formación integral de futbolistas en Chula Vista: fútbol recreativo, formativo, competitivo, entrenamiento personalizado y fútbol inclusivo.',
    // =========================================================================
    // GENERAL
    // =========================================================================
    menu: {
      inicio: 'INICIO',
      academia: 'ACADEMIA',
      metodologia: 'METODOLOGÍA',
      programas: 'PROGRAMAS',
      competencias: 'COMPETENCIAS',
      informacion: 'INFORMACIÓN',
      contacto: 'CONTACTO',
      abrirMenu: 'Abrir el menú',
      cerrarMenu: 'Cerrar el menú',
      idioma: 'Idioma',
      irA: 'Ir a la sección',
    },

    // =========================================================================
    // PORTADA · imagen 1
    // =========================================================================
    inicio: {
      lema: 'DISCIPLINE CREATES FREEDOM.',
      titulo1: 'MÁS QUE',
      titulo2: 'FÚTBOL',
      subtitulo: 'FORMANDO JUGADORES PARA LA VIDA',
      valores: 'TÉCNICA · INTELIGENCIA DE JUEGO · DISCIPLINA · VALORES',
      boton: 'CONOCE NUESTROS PROGRAMAS',
      desliza: 'DESLIZA',
    },

    // Los cinco pilares que van al pie de la portada.
    pilares: [
      { titulo: 'FÚTBOL RECREATIVO', icono: 'balon' },
      { titulo: 'FÚTBOL COMPETITIVO', icono: 'barras' },
      { titulo: 'ENTRENAMIENTO PERSONALIZADO', icono: 'diana' },
      { titulo: 'FÚTBOL INCLUSIVO', icono: 'personas' },
      { titulo: 'TORNEOS Y PROYECTOS', icono: 'trofeo' },
    ],

    // =========================================================================
    // ACADEMIA · imagen 6
    // =========================================================================
    academia: {
      /** El titular, partido en dos renglones. */
      titulo1: 'FORMACIÓN INTEGRAL',
      titulo2: 'DENTRO Y FUERA DE LA CANCHA',
      /** Los rotulos de la mision y la vision. */
      misionEtiqueta: 'NUESTRA',
      visionEtiqueta: 'NUESTRA',
      fotoAlt: 'Un jugador entrenando entre conos al atardecer',
      fotoAltMision: 'Una jugadora de espaldas con el balón al atardecer',
      etiqueta: 'ACADEMIA',
      titulo: 'FORMACIÓN INTEGRAL DENTRO Y FUERA DE LA CANCHA',
      texto:
        'En M Soccer Academy creemos en una formación que va más allá del fútbol. Desarrollamos jugadores con técnica, inteligencia de juego, disciplina, valores y un fuerte sentido de comunidad.',
      boton: 'CONOCE NUESTRA ACADEMIA',
      // Los cuatro valores con su icono.
      valores: [
        { titulo: 'TÉCNICA Y TÁCTICA', icono: 'balon' },
        { titulo: 'INTELIGENCIA DE JUEGO', icono: 'cerebro' },
        { titulo: 'VALORES Y DISCIPLINA', icono: 'personas' },
        { titulo: 'DESARROLLO INTEGRAL', icono: 'barras' },
      ],
      misionTitulo: 'NUESTRA MISIÓN',
      mision:
        'Formar jugadores íntegros, competentes y con valores, que utilicen el fútbol como herramienta de crecimiento personal y positivo impacto en su comunidad.',
      visionTitulo: 'NUESTRA VISIÓN',
      vision:
        'Ser una academia reconocida por su excelencia en la formación de jugadores, por su metodología innovadora y por contribuir al desarrollo de mejores personas dentro y fuera de la cancha.',
      /** El cierre en cursiva con el que termina la sección de competencias. */
      firma: 'Más que Fútbol',
    },

    // =========================================================================
    // METODOLOGÍA · imagen 2
    // =========================================================================
    metodologia: {
      /** El titular, partido en dos renglones. */
      titulo1: 'ENTRENAMIENTO REAL',
      titulo2: 'PARA UN DESARROLLO INTEGRAL',
      /** El titular del sistema hibrido. */
      sistema1: 'UN SISTEMA',
      sistema2: 'DE FORMACIÓN',
      sistema3: 'HÍBRIDO',
      fotoAlt: 'Una jugadora de espaldas con el balón bajo la luz del atardecer',
      etiqueta: 'METODOLOGÍA',
      titulo: 'ENTRENAMIENTO REAL PARA UN DESARROLLO INTEGRAL',
      texto:
        'Nuestra metodología combina la técnica individual, la táctica en contexto de juego y la formación de valores, con herramientas modernas para potenciar el desarrollo de cada jugador.',
      boton: 'CONOCE NUESTRA METODOLOGÍA',
      pilares: [
        { titulo: 'ENTRENAMIENTO EN CANCHA', icono: 'corriendo' },
        { titulo: 'APOYO DIGITAL', icono: 'pantalla' },
        { titulo: 'DESARROLLO INTEGRAL', icono: 'cerebro' },
        { titulo: 'EVALUACIÓN Y SEGUIMIENTO', icono: 'barras' },
      ],
      sistemaTitulo: 'UN SISTEMA DE FORMACIÓN HÍBRIDO',
      sistemaTexto:
        'Integramos el entrenamiento presencial en cancha con recursos digitales que refuerzan el aprendizaje, permitiendo un desarrollo continuo dentro y fuera del campo.',
      sistemaPuntos: [
        {
          titulo: 'ENTRENAMIENTO EN CANCHA',
          texto: 'Técnica, táctica y toma de decisiones en contexto de juego.',
          icono: 'corriendo',
        },
        {
          titulo: 'APOYO DIGITAL',
          texto: 'Videos, análisis y tareas personalizadas para continuar el aprendizaje.',
          icono: 'pantalla',
        },
        {
          titulo: 'EVALUACIÓN CONSTANTE',
          texto: 'Seguimiento del progreso técnico, táctico y conductual.',
          icono: 'barras',
        },
        {
          titulo: 'FORMACIÓN DE VALORES',
          texto: 'Disciplina, respeto, compromiso y trabajo en equipo.',
          icono: 'personas',
        },
      ],
    },

    // =========================================================================
    // PROGRAMAS · imagen 5
    // =========================================================================
    programas: {
      /** El titular grande de la seccion. */
      titulo1: 'PROGRAMAS',
      fotoAlt: 'Un jugador conduciendo el balón al atardecer',
      etiqueta: 'PROGRAMAS',
      titulo: 'FORMACIÓN PARA CADA JUGADOR',
      texto:
        'Programas diseñados para desarrollar habilidades, valores y confianza, en un ambiente positivo, exigente e inclusivo.',
      boton: 'CONOCE NUESTROS PROGRAMAS',
      lista: [
        {
          titulo: 'FÚTBOL RECREATIVO',
          texto:
            'Un espacio para disfrutar del fútbol, aprender y desarrollar habilidades en un ambiente divertido, seguro y positivo.',
          icono: 'balon',
        },
        {
          titulo: 'FÚTBOL FORMATIVO',
          texto:
            'Desarrollo técnico y táctico en contexto de juego, fortaleciendo la inteligencia de juego, la toma de decisiones y los valores.',
          icono: 'personas',
        },
        {
          titulo: 'FÚTBOL COMPETITIVO',
          texto:
            'Programa de alto desarrollo para jugadores que buscan competir en ligas y torneos, con una formación integral y exigente.',
          icono: 'barras',
        },
        {
          titulo: 'ENTRENAMIENTO PERSONALIZADO',
          texto:
            'Sesiones individuales o en pequeños grupos enfocadas en objetivos específicos de técnica, táctica, físico y mental.',
          icono: 'diana',
        },
        {
          titulo: 'FÚTBOL INCLUSIVO',
          texto:
            'Un programa para todos. Adaptamos el entrenamiento a las necesidades de cada jugador, promoviendo la inclusión, el respeto y el desarrollo personal.',
          icono: 'corazon',
        },
      ],
      incluyenTitulo: 'TODOS NUESTROS PROGRAMAS',
      incluyenDestacado: 'INCLUYEN',
      incluyen: [
        { titulo: 'ENTRENADORES CALIFICADOS', icono: 'personas' },
        { titulo: 'METODOLOGÍA PROPIA', icono: 'diana' },
        { titulo: 'SEGUIMIENTO DEL PROGRESO', icono: 'barras' },
        { titulo: 'VALORES Y DISCIPLINA', icono: 'diamante' },
      ],
    },

    // =========================================================================
    // COMPETENCIAS · imagen 7
    // =========================================================================
    competencias: {
      /** El titular grande de la seccion. */
      titulo1: 'COMPETICIONES',
      fotoAlt: 'El equipo levantando el trofeo entre confeti',
      etiqueta: 'COMPETENCIAS',
      titulo: 'COMPETICIONES',
      texto: 'Más que partidos, son oportunidades para crecer.',
      etiquetaLateral: 'JUGAR · APRENDER · CRECER · PERTENECER',
      lista: [
        { titulo: 'LIGAS RECREACIONALES', texto: 'Fútbol para todos.', icono: 'balon' },
        { titulo: 'LIGAS COMPETITIVAS', texto: 'Un mayor desafío.', icono: 'trofeo' },
        {
          titulo: 'TORNEOS INTERNACIONALES',
          texto: 'Experiencias sin fronteras.',
          icono: 'globo',
        },
        { titulo: 'FÚTBOL SALA', texto: 'Técnica en cada espacio.', icono: 'cancha' },
      ],
      boton: 'CONOCE MÁS',
      paneles: [
        { titulo: 'EXPERIENCIAS INTERNACIONALES' },
        { titulo: 'TRABAJO EN EQUIPO' },
        { titulo: 'NUEVOS DESAFÍOS' },
        { titulo: 'CRECIMIENTO DENTRO Y FUERA DE LA CANCHA' },
      ],
      cierre1: 'UN MISMO OBJETIVO,',
      cierre2: 'MEJORES PERSONAS',
    },

    // =========================================================================
    // INFORMACIÓN E INSCRIPCIONES · imagen 3
    // =========================================================================
    informacion: {
      etiqueta: 'INFORMACIÓN',
      titulo1: 'FORMA PARTE DE',
      titulo2: 'M SOCCER',
      titulo3: 'ACADEMY',
      texto: 'Conoce nuestros programas, horarios y proceso de inscripción.',
      /** El texto de cada tarjeta según la situación. */
      tarjetaEscribenos: 'Escríbenos directamente.',
      tarjetaCorreo: 'Envíanos un mensaje.',
      tarjetaSinCorreo: 'Escríbenos desde el formulario.',
      tarjetaHorarios: 'Consulta los entrenamientos.',
      tarjetaHorariosPendiente: 'Por confirmar.',
      /** El asunto del correo, que es lo que ve quien lo recibe. */
      asuntoCorreo: 'Información · M Soccer Academy',
      /** El título del mapa incrustado. */
      tituloMapa: 'Mapa de la academia',
      /** El texto alternativo de la fotografía. */
      fotoAlt: 'Dos jugadores caminando por la cancha con las banderas del club al fondo',
      tarjetas: [
        {
          titulo: 'CORREO',
          texto: 'Envíanos un mensaje.',
          icono: 'sobre',
          accion: 'correo',
        },
        {
          titulo: 'UBICACIÓN',
          texto: 'Visítanos en el campo.',
          icono: 'pin',
          accion: 'mapa',
        },
        {
          titulo: 'HORARIOS',
          texto: 'Conoce nuestros entrenamientos.',
          icono: 'calendario',
          accion: 'horarios',
        },
      ],
      boton: 'SOLICITA INFORMACIÓN',
    },

    // =========================================================================
    // CONTACTO E INSCRIPCIONES · imagen 4
    // =========================================================================
    contacto: {
      /** El rotulo de los horarios y el boton del aviso. */
      horariosEtiqueta: 'HORARIOS',
      botonHorarios: 'Información',
      fotoAlt: 'Un jugador conduciendo el balón en un torneo internacional',
      abrirMapa: 'Abrir en Google Maps',
      tituloMapa: 'Mapa de la academia',
      etiqueta: 'CONTACTO E',
      titulo: 'INSCRIPCIONES',
      texto: 'Tu próxima etapa en el fútbol comienza aquí.',
      // La firma manuscrita que va encima del botón.
      firma1: 'Da el',
      firma2: 'primer paso',
      boton: 'REGÍSTRATE AQUÍ',
      procesoEtiqueta: 'PROCESO DE INSCRIPCIÓN',
      proceso: [
        {
          titulo: '1. COMPLETA TUS DATOS',
          texto: 'Es rápido y fácil.',
          icono: 'lista',
        },
        {
          titulo: '2. CONOCE TU PROGRAMA',
          texto: 'Te orientamos para encontrar el ideal para ti.',
          icono: 'personas',
        },
        {
          titulo: '3. RECIBE INFORMACIÓN PARA COMENZAR',
          texto: 'Nuestro equipo se pondrá en contacto contigo.',
          icono: 'sobre',
        },
      ],
      ubicacionTitulo: 'UBICACIÓN',
      siguenos: 'SÍGUENOS',
    },

    // =========================================================================
    // FORMULARIO
    // =========================================================================
    form: {
      titulo: 'Solicita información',
      bajada:
        'Llena estos datos y nos ponemos en contacto contigo. Los campos con asterisco son obligatorios.',
      tutor: 'Nombre del padre, madre o tutor',
      jugador: 'Nombre del jugador',
      edad: 'Edad del jugador',
      telefono: 'Teléfono',
      correo: 'Correo electrónico',
      programa: 'Programa de interés',
      programaPlaceholder: 'Elige un programa',
      mensaje: 'Mensaje',
      mensajePlaceholder: 'Cuéntanos qué necesitas saber.',
      enviar: 'Enviar solicitud',
      enviando: 'Enviando…',
      obligatorio: 'Este campo es obligatorio.',
      correoInvalido: 'Escribe un correo válido.',
      telefonoInvalido: 'Escribe un teléfono de al menos 10 dígitos.',
      edadInvalida: 'Escribe una edad entre 4 y 60 años.',
      // Lo que se dice cuando NO hay endpoint configurado.
      sinDestinoTitulo: 'Falta conectar el formulario',
      sinDestinoTexto:
        'El formulario está listo, pero todavía no se ha configurado a dónde enviar las solicitudes, así que NO se envió nada. Para terminarlo, pon la dirección del servicio en el archivo de configuración. Mientras tanto, escríbenos por correo.',
      exitoTitulo: 'Solicitud enviada',
      exitoTexto:
        'Recibimos tus datos. Nos pondremos en contacto contigo muy pronto.',
      errorTitulo: 'No se pudo enviar',
      errorTexto:
        'Hubo un problema al enviar la solicitud. Intenta de nuevo o escríbenos por correo.',
      /** El aviso que sale cuando hay un campo mal puesto. */
      hayUnCampo: 'Hay un campo por corregir. Está marcado abajo.',
      /** El asunto del correo del formulario. */
      correoAsunto: 'Solicitud de información · M Soccer Academy',
      reintentar: 'Intentar de nuevo',
      escribirCorreo: 'Escribir por correo',
    },

    // =========================================================================
    // AVISO DE DATOS PENDIENTES
    // =========================================================================
    /**
     * LA VENTANA PARA INSTALAR LA PÁGINA EN EL TELÉFONO
     *
     * -----------------------------------------------------------------------------
     * POR QUÉ EXISTE
     * -----------------------------------------------------------------------------
     * Una página web se puede instalar en el teléfono como si fuera una aplicación:
     * queda con su icono en la pantalla de inicio, abre a pantalla completa y sin
     * la barra del navegador.
     *
     * Pero el navegador no lo ofrece de forma visible: hay que buscarlo en su menú.
     * Casi nadie lo encuentra. Esta ventana lo pone delante.
     *
     * -----------------------------------------------------------------------------
     * LOS DOS CAMINOS
     * -----------------------------------------------------------------------------
     *   · Android y Chrome → el navegador da un aviso que se puede disparar desde
     *     código, así que el botón instala de verdad, en un toque.
     *   · iPhone y Safari → NO existe ese aviso. Ahí solo se puede explicar los
     *     pasos, porque Apple no deja que una página instale sola. Se le dice dónde
     *     está el botón, con el nombre exacto en su idioma.
     */
    /**
     * LA GALERÍA
     * -----------------------------------------------------------------------------
     * Las fotos las pone el administrador desde el panel. Si no hay ninguna, la
     * sección no se muestra: no queda un hueco vacío en la página.
     */
    /** LAS NOTIFICACIONES AL TELÉFONO */
    notificaciones: {
      titulo: 'Recibe los avisos',
      invitacion:
        'Activa las notificaciones y te avisamos de los horarios, los torneos y cualquier cambio. Llega al teléfono aunque tengas la página cerrada.',
      boton: 'Activar los avisos',
      activando: 'Activando…',
      activadas: 'Vas a recibir los avisos de la academia.',
      listo: 'Avisos activados',
      bloqueadas:
        'Tienes los avisos bloqueados para esta página. Para activarlos, entra en los ajustes de tu navegador, busca esta página y permite las notificaciones.',
    },

    galeria: {
      etiqueta: 'GALERÍA',
      titulo: 'NUESTROS MOMENTOS',
      texto:
        'Entrenamientos, torneos y todo lo que pasa dentro y fuera de la cancha. Toca una foto para verla en grande.',
      ampliar: 'Ver la foto en grande',
      cerrar: 'Cerrar',
      /** Lo que se dice cuando todavía no hay fotos. */
      vacia:
        'Todavía no hay fotos. Se ponen desde el panel de administración, en la pestaña Galería.',
    },

    instalar: {
      titulo: 'Lleva la academia en tu teléfono',
      texto:
        'Instala la página y queda como una aplicación: su icono en la pantalla de inicio, abre a pantalla completa y carga al instante.',
      boton: 'Instalar',
      ahoraNo: 'Ahora no',
      ventajas: [
        'Su icono en la pantalla de inicio',
        'Abre sin la barra del navegador',
        'Carga al instante, aunque no haya señal',
      ],
      /** Para iPhone, que no deja instalar desde la página. */
      tituloIphone: 'Para instalarla en tu iPhone',
      pasosIphone: [
        'Toca el botón Compartir, abajo en el centro.',
        'Baja y elige «Añadir a pantalla de inicio».',
        'Toca «Añadir», arriba a la derecha.',
      ],
      /** Para Android sin el aviso del navegador. */
      tituloAndroid: 'Para instalarla en tu Android',
      pasosAndroid: [
        'Toca el menú del navegador, los tres puntos de arriba.',
        'Elige «Instalar aplicación» o «Añadir a pantalla de inicio».',
        'Confirma tocando «Instalar».',
      ],
      /** Lo que se oye al instalarla. */
      instalada: 'Listo. Ya la tienes en tu teléfono.',
      cerrar: 'Cerrar',
    },

    pendientes: {
      titulo: 'Datos por completar',
      texto:
        'Esta página está terminada y funciona, pero faltan datos oficiales que no se pueden inventar. Mientras no se llenen, algunos enlaces avisan en lugar de llevar a un lugar equivocado.',
      lista: 'Falta por poner:',
      cerrar: 'Entendido',
      verArchivo: 'Se configura en src/config.ts',
    },

    // =========================================================================
    // ACCESIBILIDAD
    // =========================================================================
    a11y: {
      logoAlt: 'Escudo de M Soccer Academy',
      irAlContenido: 'Ir al contenido',
      volverArriba: 'Volver arriba',
      enlaceExterno: 'Abre en una ventana nueva',
    },
  },

  // ===========================================================================
  // INGLÉS
  // ===========================================================================
  en: {
    metaTitulo: 'M SOCCER ACADEMY · More than soccer',
    metaDescripcion:
      'Comprehensive soccer training in Chula Vista: recreational, developmental and competitive soccer, one-on-one training and inclusive soccer.',
    menu: {
      inicio: 'HOME',
      academia: 'ACADEMY',
      metodologia: 'METHODOLOGY',
      programas: 'PROGRAMS',
      competencias: 'COMPETITIONS',
      informacion: 'INFORMATION',
      contacto: 'CONTACT',
      abrirMenu: 'Open menu',
      cerrarMenu: 'Close menu',
      idioma: 'Language',
      irA: 'Go to section',
    },

    inicio: {
      lema: 'DISCIPLINE CREATES FREEDOM.',
      titulo1: 'MORE THAN',
      titulo2: 'SOCCER',
      subtitulo: 'BUILDING PLAYERS FOR LIFE',
      valores: 'TECHNIQUE · GAME INTELLIGENCE · DISCIPLINE · VALUES',
      boton: 'DISCOVER OUR PROGRAMS',
      desliza: 'SCROLL',
    },

    pilares: [
      { titulo: 'RECREATIONAL SOCCER', icono: 'balon' },
      { titulo: 'COMPETITIVE SOCCER', icono: 'barras' },
      { titulo: 'PERSONALIZED TRAINING', icono: 'diana' },
      { titulo: 'INCLUSIVE SOCCER', icono: 'personas' },
      { titulo: 'TOURNAMENTS & PROJECTS', icono: 'trofeo' },
    ],

    academia: {
      titulo1: 'COMPREHENSIVE TRAINING',
      titulo2: 'ON AND OFF THE PITCH',
      misionEtiqueta: 'OUR',
      visionEtiqueta: 'OUR',
      fotoAlt: 'A player training between cones at sunset',
      fotoAltMision: 'A player seen from behind with the ball at sunset',
      etiqueta: 'ACADEMY',
      titulo: 'COMPREHENSIVE TRAINING ON AND OFF THE PITCH',
      texto:
        'At M Soccer Academy we believe in training that goes beyond soccer. We develop players with technique, game intelligence, discipline, values and a strong sense of community.',
      boton: 'DISCOVER OUR ACADEMY',
      valores: [
        { titulo: 'TECHNIQUE & TACTICS', icono: 'balon' },
        { titulo: 'GAME INTELLIGENCE', icono: 'cerebro' },
        { titulo: 'VALUES & DISCIPLINE', icono: 'personas' },
        { titulo: 'COMPREHENSIVE DEVELOPMENT', icono: 'barras' },
      ],
      misionTitulo: 'OUR MISSION',
      mision:
        'To build players of integrity, competence and values, who use soccer as a tool for personal growth and positive impact in their community.',
      visionTitulo: 'OUR VISION',
      vision:
        'To be an academy recognized for excellence in player development, for its innovative methodology, and for helping shape better people on and off the pitch.',
      firma: 'More than Soccer',
    },

    metodologia: {
      titulo1: 'REAL TRAINING',
      titulo2: 'FOR COMPREHENSIVE DEVELOPMENT',
      sistema1: 'A HYBRID',
      sistema2: 'TRAINING',
      sistema3: 'SYSTEM',
      fotoAlt: 'A player seen from behind with the ball in the sunset light',
      etiqueta: 'METHODOLOGY',
      titulo: 'REAL TRAINING FOR COMPREHENSIVE DEVELOPMENT',
      texto:
        'Our methodology combines individual technique, tactics in game context and values education, with modern tools to boost every player’s development.',
      boton: 'DISCOVER OUR METHODOLOGY',
      pilares: [
        { titulo: 'ON-PITCH TRAINING', icono: 'corriendo' },
        { titulo: 'DIGITAL SUPPORT', icono: 'pantalla' },
        { titulo: 'COMPREHENSIVE DEVELOPMENT', icono: 'cerebro' },
        { titulo: 'ASSESSMENT & FOLLOW-UP', icono: 'barras' },
      ],
      sistemaTitulo: 'A HYBRID TRAINING SYSTEM',
      sistemaTexto:
        'We combine on-pitch training with digital resources that reinforce learning, allowing continuous development on and off the field.',
      sistemaPuntos: [
        {
          titulo: 'ON-PITCH TRAINING',
          texto: 'Technique, tactics and decision-making in game context.',
          icono: 'corriendo',
        },
        {
          titulo: 'DIGITAL SUPPORT',
          texto: 'Videos, analysis and personalized tasks to keep learning.',
          icono: 'pantalla',
        },
        {
          titulo: 'CONSTANT ASSESSMENT',
          texto: 'Tracking of technical, tactical and behavioral progress.',
          icono: 'barras',
        },
        {
          titulo: 'VALUES EDUCATION',
          texto: 'Discipline, respect, commitment and teamwork.',
          icono: 'personas',
        },
      ],
    },

    programas: {
      titulo1: 'PROGRAMS',
      fotoAlt: 'A player dribbling the ball at sunset',
      etiqueta: 'PROGRAMS',
      titulo: 'TRAINING FOR EVERY PLAYER',
      texto:
        'Programs designed to develop skills, values and confidence, in a positive, demanding and inclusive environment.',
      boton: 'DISCOVER OUR PROGRAMS',
      lista: [
        {
          titulo: 'RECREATIONAL SOCCER',
          texto:
            'A space to enjoy soccer, learn and develop skills in a fun, safe and positive environment.',
          icono: 'balon',
        },
        {
          titulo: 'DEVELOPMENTAL SOCCER',
          texto:
            'Technical and tactical development in game context, strengthening game intelligence, decision-making and values.',
          icono: 'personas',
        },
        {
          titulo: 'COMPETITIVE SOCCER',
          texto:
            'A high-performance program for players who want to compete in leagues and tournaments, with demanding, comprehensive training.',
          icono: 'barras',
        },
        {
          titulo: 'PERSONALIZED TRAINING',
          texto:
            'Individual or small-group sessions focused on specific technical, tactical, physical and mental goals.',
          icono: 'diana',
        },
        {
          titulo: 'INCLUSIVE SOCCER',
          texto:
            'A program for everyone. We adapt training to each player’s needs, promoting inclusion, respect and personal growth.',
          icono: 'corazon',
        },
      ],
      incluyenTitulo: 'ALL OUR PROGRAMS',
      incluyenDestacado: 'INCLUDE',
      incluyen: [
        { titulo: 'QUALIFIED COACHES', icono: 'personas' },
        { titulo: 'OUR OWN METHODOLOGY', icono: 'diana' },
        { titulo: 'PROGRESS TRACKING', icono: 'barras' },
        { titulo: 'VALUES & DISCIPLINE', icono: 'diamante' },
      ],
    },

    competencias: {
      titulo1: 'COMPETITIONS',
      fotoAlt: 'The team lifting the trophy among confetti',
      etiqueta: 'COMPETITIONS',
      titulo: 'COMPETITIONS',
      texto: 'More than matches, they are opportunities to grow.',
      etiquetaLateral: 'PLAY · LEARN · GROW · BELONG',
      lista: [
        {
          titulo: 'RECREATIONAL LEAGUES',
          texto: 'Soccer for everyone.',
          icono: 'balon',
        },
        { titulo: 'COMPETITIVE LEAGUES', texto: 'A bigger challenge.', icono: 'trofeo' },
        {
          titulo: 'INTERNATIONAL TOURNAMENTS',
          texto: 'Experiences without borders.',
          icono: 'globo',
        },
        { titulo: 'FUTSAL', texto: 'Technique in every space.', icono: 'cancha' },
      ],
      boton: 'LEARN MORE',
      paneles: [
        { titulo: 'INTERNATIONAL EXPERIENCES' },
        { titulo: 'TEAMWORK' },
        { titulo: 'NEW CHALLENGES' },
        { titulo: 'GROWTH ON AND OFF THE PITCH' },
      ],
      cierre1: 'ONE SAME GOAL,',
      cierre2: 'BETTER PEOPLE',
    },

    informacion: {
      etiqueta: 'INFORMATION',
      titulo1: 'BE PART OF',
      titulo2: 'M SOCCER',
      titulo3: 'ACADEMY',
      texto: 'Discover our programs, schedules and registration process.',
      tarjetaEscribenos: 'Message us directly.',
      tarjetaCorreo: 'Send us an email.',
      tarjetaSinCorreo: 'Write to us from the form.',
      tarjetaHorarios: 'See the training times.',
      tarjetaHorariosPendiente: 'To be confirmed.',
      asuntoCorreo: 'Information · M Soccer Academy',
      tituloMapa: 'Map of the academy',
      fotoAlt: 'Two players walking across the pitch with the club flags behind them',
      tarjetas: [
        { titulo: 'EMAIL', texto: 'Send us a message.', icono: 'sobre', accion: 'correo' },
        { titulo: 'LOCATION', texto: 'Visit us at the field.', icono: 'pin', accion: 'mapa' },
        {
          titulo: 'SCHEDULES',
          texto: 'See our training times.',
          icono: 'calendario',
          accion: 'horarios',
        },
      ],
      boton: 'REQUEST INFORMATION',
    },

    contacto: {
      horariosEtiqueta: 'SCHEDULES',
      botonHorarios: 'Information',
      fotoAlt: 'A player dribbling the ball at an international tournament',
      abrirMapa: 'Open in Google Maps',
      tituloMapa: 'Map of the academy',
      etiqueta: 'CONTACT &',
      titulo: 'REGISTRATION',
      texto: 'Your next stage in soccer starts here.',
      firma1: 'Take the',
      firma2: 'first step',
      boton: 'REGISTER HERE',
      procesoEtiqueta: 'REGISTRATION PROCESS',
      proceso: [
        { titulo: '1. FILL IN YOUR DETAILS', texto: 'It is quick and easy.', icono: 'lista' },
        {
          titulo: '2. FIND YOUR PROGRAM',
          texto: 'We help you find the right fit.',
          icono: 'personas',
        },
        {
          titulo: '3. GET THE INFO TO START',
          texto: 'Our team will get in touch with you.',
          icono: 'sobre',
        },
      ],
      ubicacionTitulo: 'LOCATION',
      siguenos: 'FOLLOW US',
    },

    form: {
      titulo: 'Request information',
      bajada:
        'Fill in these details and we will get in touch. Fields marked with an asterisk are required.',
      tutor: 'Parent or guardian name',
      jugador: 'Player name',
      edad: 'Player age',
      telefono: 'Phone',
      correo: 'Email',
      programa: 'Program of interest',
      programaPlaceholder: 'Choose a program',
      mensaje: 'Message',
      mensajePlaceholder: 'Tell us what you would like to know.',
      enviar: 'Send request',
      enviando: 'Sending…',
      obligatorio: 'This field is required.',
      correoInvalido: 'Enter a valid email address.',
      telefonoInvalido: 'Enter a phone number with at least 10 digits.',
      edadInvalida: 'Enter an age between 4 and 60.',
      sinDestinoTitulo: 'The form is not connected yet',
      sinDestinoTexto:
        'The form is ready, but no destination has been configured yet, so NOTHING was sent. To finish it, add the service address in the configuration file. In the meantime, email us.',
      exitoTitulo: 'Request sent',
      exitoTexto: 'We received your details. We will contact you very soon.',
      errorTitulo: 'Could not send',
      errorTexto:
        'There was a problem sending your request. Try again or email us.',
      hayUnCampo: 'One field needs fixing. It is marked below.',
      correoAsunto: 'Information request · M Soccer Academy',
      reintentar: 'Try again',
      escribirCorreo: 'Send an email',
    },

    notificaciones: {
      titulo: 'Get the updates',
      invitacion:
        'Turn on notifications and we will tell you about schedules, tournaments and any changes. It reaches your phone even with the page closed.',
      boton: 'Turn on updates',
      activando: 'Turning on…',
      activadas: 'You will get the academy updates.',
      listo: 'Updates on',
      bloqueadas:
        'Updates are blocked for this page. To turn them on, open your browser settings, find this page and allow notifications.',
    },

    galeria: {
      etiqueta: 'GALLERY',
      titulo: 'OUR MOMENTS',
      texto:
        'Training, tournaments and everything that happens on and off the pitch. Tap a photo to see it full size.',
      ampliar: 'See the photo full size',
      cerrar: 'Close',
      vacia: 'There are no photos yet. They are added from the admin panel, in the Gallery tab.',
    },

    instalar: {
      titulo: 'Keep the academy on your phone',
      texto:
        'Install the site and it works like an app: its icon on your home screen, opens full screen and loads instantly.',
      boton: 'Install',
      ahoraNo: 'Not now',
      ventajas: [
        'Its icon on your home screen',
        'Opens without the browser bar',
        'Loads instantly, even with no signal',
      ],
      tituloIphone: 'To install it on your iPhone',
      pasosIphone: [
        'Tap the Share button, at the bottom centre.',
        'Scroll down and choose “Add to Home Screen”.',
        'Tap “Add”, at the top right.',
      ],
      tituloAndroid: 'To install it on your Android',
      pasosAndroid: [
        'Tap the browser menu, the three dots at the top.',
        'Choose “Install app” or “Add to Home screen”.',
        'Confirm by tapping “Install”.',
      ],
      instalada: 'Done. It is now on your phone.',
      cerrar: 'Close',
    },

    pendientes: {
      titulo: 'Details still to be filled in',
      texto:
        'This site is finished and working, but some official details are missing and cannot be invented. Until they are filled in, some links will explain instead of taking you somewhere wrong.',
      lista: 'Still to be added:',
      cerrar: 'Got it',
      verArchivo: 'Set in src/config.ts',
    },

    a11y: {
      logoAlt: 'M Soccer Academy crest',
      irAlContenido: 'Skip to content',
      volverArriba: 'Back to top',
      enlaceExterno: 'Opens in a new window',
    },
  },
} as const;

export type Idioma = keyof typeof TEXTOS;

/** El tipo de los textos, para que los componentes sepan qué reciben. */
export type Textos = (typeof TEXTOS)['es'];
